import React, { useState, useEffect } from 'react';
import Modal from './index';
import style from './modal.module.scss';
import Button from 'controls/buttons/submit';
import { useSearchControl } from 'hooks/useSearchControl';
import { Tag, Tags } from 'store/types';
import isEmpty from 'lodash-es/isEmpty';
import { useAppDispatch } from 'store/hooks';
import cx from 'classnames';
import size from 'lodash-es/size';
import map from 'lodash-es/map';
import some from 'lodash-es/some';
import reduce from 'lodash-es/reduce';
import keys from 'lodash-es/keys';
import omit from 'lodash-es/omit';
import { editTag, fetchAllTags, fetchTopTags } from 'store/tagsSlice';
import HelperSearch from 'controls/search/helperSearch/helperSearch';
import { setGlobalModal } from 'store/transitionSlice';
import IconPlus from 'icons/plus';
import { v4 as uuid } from 'uuid';
import DeleteIcon from 'icons/delete';
import { getDeclension } from 'utils/utils';

const MAX_TAGS = 4;

export default function EditTagModal({
  tag,
  onCancel,
}: {
  onCancel: () => void;
  tag: Tag;
}) {
  const dispatch = useAppDispatch();
  const [tagsMap, setTagsMap] = useState<{ [key: string]: Tags }>({
    [uuid()]: {},
  });
  const [disabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const collectTags = () => {
    return reduce(
      tagsMap,
      (acc: Tags, tags) => {
        return { ...acc, ...tags };
      },
      {}
    );
  };
  const collectedTags = collectTags();
  const isSelected = !isEmpty(collectedTags);
  const isTagsLimit = size(tagsMap) >= MAX_TAGS;

  useEffect(() => {
    setDisabled(some(tagsMap, (tags) => size(tags) < 1));
  }, [tagsMap]);

  const onSubmit = async () => {
    setIsLoading(true);

    const result = await dispatch(
      editTag({
        old: tag,
        new: collectedTags,
      })
    );

    if (result.meta.requestStatus === 'fulfilled') {
      await dispatch(fetchTopTags(null));
      await dispatch(fetchAllTags({}));

      dispatch(setGlobalModal(null));
    }
  };

  let i = 0;
  return (
    <Modal open={true} onClose={onCancel}>
      <div
        className={cx(
          style.modalScrollWrapper,
          'adaptive-editTagModal__modalScrollWrapper'
        )}
      >
        <form
          className={cx(style.editTag, 'adaptive-editTagModal__form')}
          onSubmit={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onSubmit();
          }}
        >
          <span className={style.mainText}>Изменение тега</span>
          <span className={style.detailedText}>
            Исправьте наименование или введите новое, чтобы изменить тег
          </span>

          <div className={style.inner}>
            <div className={style.searchBlock}>
              <div className={style.tags}>
                <span className={style.tagType}>Изначальный тег</span>
                <HelperSearch
                  isDisabled={true}
                  className={style.search}
                  initialSearch={tag.name}
                ></HelperSearch>
              </div>
            </div>

            <div className={style.searchBlock}>
              <div className={style.tags}>
                <span className={style.tagType}>Измененный тег</span>
                {map(keys(tagsMap), (key) => {
                  return (
                    <div className={style.searchBlock} key={key}>
                      <div className={style.search}>
                        <HelperSearch
                          initialSearch={tag.name}
                          // Отключим секцию популярных тегов, чтобы не вводить в заблуждение и не плодить лишних надписей
                          sectionsOrder={['selected', 'available', 'create']}
                          overrideSectionTexts={{
                            selected: 'Выбрано',
                            available: 'Объединить с тегом',
                            create: 'Переименовать тег',
                          }}
                          tagsStrategy={useSearchControl.bind(null, {
                            onUpdate: (tags) => {
                              setTagsMap((oldMap) => {
                                return { ...oldMap, [key]: tags };
                              });
                            },
                            limit: 1,
                          })}
                        ></HelperSearch>
                      </div>
                      {size(tagsMap) > 1 ? (
                        <DeleteIcon
                          onClick={() =>
                            setTagsMap((oldMap) => {
                              return { ...omit(oldMap, key) };
                            })
                          }
                        ></DeleteIcon>
                      ) : (
                        ''
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                onClick={() => {
                  if (isTagsLimit) {
                    return;
                  }
                  setTagsMap((old) => {
                    return { ...old, [uuid()]: {} };
                  });
                }}
                className={cx(
                  style.add,
                  'clickable-text',
                  disabled || isTagsLimit ? 'blockDisabled' : ''
                )}
              >
                <IconPlus></IconPlus>
                <span>Добавить тег</span>
              </div>
            </div>

            {isSelected && (
              <div className={style.explanation}>
                <span>
                  Тег <b>{`«${tag.name}»`} </b>
                </span>
                <span className={style.explanationChip}>будет изменен</span>
                <span>
                  {' '}
                  на{' '}
                  {getDeclension(size(collectedTags), [
                    'тег',
                    'теги',
                    'теги',
                  ])}{' '}
                  {map(collectedTags, (collectedTag) => {
                    i += 1;
                    return (
                      <b key={collectedTag.name}>{`«${collectedTag.name}»${
                        i < size(collectedTags) ? ', ' : ''
                      }`}</b>
                    );
                  })}
                </span>
              </div>
            )}
          </div>

          <div className={style.buttons}>
            <Button
              disabled={disabled}
              className={style.close}
              wrapper={{
                loading: isLoading,
              }}
            >
              {!isLoading && 'Сохранить'}
            </Button>
            <Button
              className={cx(style.close, 'btn-cancel')}
              onClick={onCancel}
            >
              Отмена
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
